// Refresh / Refresh sheet / Refresh workbook in the task pane: the ribbon's
// refresh commands (commands.ts), reachable without a manifest update. The
// production manifest is centrally deployed (Microsoft 365 admin center), so
// new ribbon buttons wait for an admin re-upload, while this pane is loaded
// live from the add-in host.
// taskpane.js is its own bundle: importing functions.ts here would give the
// pane a second copy that keeps none of the cells' calls, so functions.js
// publishes its refresh() on the shared runtime's window instead.
import type { FormulaScope } from "../lib/excelPort";

export type RefreshFn = (scope: FormulaScope) => Promise<number>;

declare global {
  /** Set by functions.js (commands.ts): the shared runtime's one refresh(). */
  // eslint-disable-next-line no-var
  var eavRefresh: RefreshFn | undefined;
}

export interface RefreshNote {
  text: string;
  kind: "ok" | "error";
}

const EMPTY: Record<FormulaScope, string> = {
  selection: "No EAV formula in the selection.",
  sheet: "No EAV formula on this sheet.",
  workbook: "No EAV formula in this workbook.",
};

/** Refresh `scope` and say how it went; never throws. */
export async function runRefresh(refresh: RefreshFn | undefined, scope: FormulaScope): Promise<RefreshNote> {
  if (!refresh) return { text: "Refresh is not available: close and reopen the Eavest panel.", kind: "error" };
  try {
    const n = await refresh(scope);
    if (!n) return { text: EMPTY[scope], kind: "ok" };
    return { text: `Refreshed ${n} formula${n === 1 ? "" : "s"}.`, kind: "ok" };
  } catch (e) {
    return { text: `Refresh failed: ${e instanceof Error ? e.message : String(e)}`, kind: "error" };
  }
}

const BUTTONS: [id: string, scope: FormulaScope][] = [
  ["refresh-selection", "selection"],
  ["refresh-sheet", "sheet"],
  ["refresh-workbook", "workbook"],
];

/** Wire the three buttons: one refresh at a time, its outcome under them. */
export function mountRefreshPane(): void {
  const note = document.getElementById("refresh-msg")!;
  const buttons = BUTTONS.map(([id]) => document.getElementById(id) as HTMLButtonElement);
  const say = (text: string, kind: "ok" | "error" | ""): void => {
    note.textContent = text;
    note.className = "msg" + (kind ? ` msg-${kind}` : "");
    note.hidden = !text;
  };
  BUTTONS.forEach(([, scope], i) =>
    buttons[i].addEventListener("click", async () => {
      buttons.forEach((b) => (b.disabled = true));
      say("Refreshing…", "");
      const r = await runRefresh(globalThis.eavRefresh, scope);
      say(r.text, r.kind);
      buttons.forEach((b) => (b.disabled = false));
    })
  );
}
